import { CURRENT_GENETIC_CONSENT_VERSION } from '../config/legalVersions'
import { makeAuthenticatedRequest } from '../utils/apiInterceptor'

export const GENETIC_CONSENT_REQUIRED = 'GENETIC_CONSENT_REQUIRED'
export const GENETIC_CONSENT_REQUIRED_EVENT = 'legal:geneticConsentRequired'
export const GENETIC_CONSENT_ACCEPTED_EVENT = 'legal:geneticConsentAccepted'

export type LegalAcceptanceMethod =
  | 'signup'
  | 'google_signin_wrap'
  | 'update'
  | 'migration'

export interface UserComplianceStatus {
  terms_version?: string
  privacy_policy_version?: string
  genetic_data_consent_at?: string
  genetic_data_consent_version?: string
  needs_tos_update?: boolean
  needs_privacy_update?: boolean
  needs_genetic_consent?: boolean
  current_genetic_consent_version?: string
}

export const legalService = {
  async acceptTerms(method: LegalAcceptanceMethod = 'update'): Promise<void> {
    await makeAuthenticatedRequest('/api/v1/legal/accept-terms', {
      method: 'POST',
      body: JSON.stringify({ acceptance_method: method }),
    })
  },

  async acceptPrivacyPolicy(method: LegalAcceptanceMethod = 'update'): Promise<void> {
    await makeAuthenticatedRequest('/api/v1/legal/accept-privacy-policy', {
      method: 'POST',
      body: JSON.stringify({ acceptance_method: method }),
    })
  },

  async acceptGeneticDataConsent(method: LegalAcceptanceMethod = 'update'): Promise<void> {
    await makeAuthenticatedRequest('/api/v1/legal/accept-genetic-consent', {
      method: 'POST',
      body: JSON.stringify({ acceptance_method: method }),
    })
  },

  /**
   * Withdraw genetic data consent. This permanently deletes the account's genetic
   * data — raw files, analysis results and derived reports — across every profile.
   * The account and its profiles remain.
   */
  async withdrawGeneticDataConsent(): Promise<{ profiles_affected: number }> {
    const response = await makeAuthenticatedRequest('/api/v1/legal/withdraw-genetic-consent', {
      method: 'POST',
      body: JSON.stringify({ confirm: true }),
    })
    return response as { profiles_affected: number }
  },

  async getUserCompliance(): Promise<UserComplianceStatus> {
    return makeAuthenticatedRequest<UserComplianceStatus>('/api/v1/legal/user-compliance')
  },

  async recordSignupAcceptance(method: 'signup' | 'google_signin_wrap'): Promise<void> {
    await Promise.all([
      this.acceptTerms(method),
      this.acceptPrivacyPolicy(method),
    ])
  },

  /**
   * Open the mid-action genetic consent banner and resolve once the user accepts.
   */
  waitForGeneticConsent(timeoutMs = 10 * 60 * 1000): Promise<void> {
    window.dispatchEvent(new CustomEvent(GENETIC_CONSENT_REQUIRED_EVENT))
    return new Promise((resolve, reject) => {
      const cleanup = () => {
        window.clearTimeout(timer)
        window.removeEventListener(GENETIC_CONSENT_ACCEPTED_EVENT, onAccepted)
      }
      const onAccepted = () => {
        cleanup()
        resolve()
      }
      const timer = window.setTimeout(() => {
        cleanup()
        reject(new Error('Genetic data consent is required before this file can be processed.'))
      }, timeoutMs)
      window.addEventListener(GENETIC_CONSENT_ACCEPTED_EVENT, onAccepted)
    })
  },

  complianceNeedsGeneticConsent(compliance: UserComplianceStatus): boolean {
    return (
      Boolean(compliance.needs_genetic_consent) ||
      compliance.genetic_data_consent_version !==
        (compliance.current_genetic_consent_version || CURRENT_GENETIC_CONSENT_VERSION)
    )
  },

  isGeneticConsentRequiredResponse(errorData: { error?: string; detail?: unknown }): boolean {
    if (errorData?.error === GENETIC_CONSENT_REQUIRED) {
      return true
    }
    const detail = typeof errorData?.detail === 'string' ? errorData.detail : ''
    return detail.includes('Genetic data consent is required')
  },

  throwGeneticConsentRequired(message?: string): never {
    const error = new Error(
      message || 'Genetic data consent is required before this file can be processed.'
    )
    ;(error as Error & { geneticConsentRequired: boolean }).geneticConsentRequired = true
    throw error
  },

  async fetchCurrentVersions(): Promise<{
    tos_version: string
    privacy_version: string
    partner_terms_version: string
    client_waiver_version: string
  }> {
    const response = await fetch('/api/v1/legal/versions')
    if (!response.ok) {
      throw new Error('Failed to fetch legal versions')
    }
    return response.json()
  },
}
